import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  BarChart3,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ArrowUpDown,
  Package,
  Percent,
  Lightbulb,
  Info,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import MainNav from "@/components/MainNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value || 0);
};

const formatPercent = (value) => {
  return `${(value || 0).toFixed(1)}%`;
};

const formatDelta = (value) => {
  if (value > 0) return `+${formatCurrency(value)}`;
  if (value < 0) return formatCurrency(value);
  return "$0";
};

// Insight badge component
function InsightBadge({ insight }) {
  const config = {
    "Frequently overridden": { color: "bg-amber-100 text-amber-700", icon: AlertTriangle },
    "Often increased": { color: "bg-green-100 text-green-700", icon: TrendingUp },
    "Often discounted": { color: "bg-red-100 text-red-700", icon: TrendingDown },
    "Stable pricing": { color: "bg-gray-100 text-gray-600", icon: CheckCircle },
  };
  
  const { color, icon: Icon } = config[insight] || config["Stable pricing"];
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      <Icon className="w-3 h-3" />
      {insight}
    </span>
  );
}

// Summary card component
function SummaryCard({ title, value, subtitle, icon: Icon, trend, description }) {
  return (
    <div className="bg-aa-card rounded-aa-lg p-5 shadow-aa-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-aa-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
            {title}
            {description && (
              <TooltipProvider delayDuration={100}>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-aa-primary transition-colors cursor-help"
                      aria-label={`About ${title}`}
                      data-testid={`pi-tooltip-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[240px] text-xs">
                    {description}
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
            )}
          </p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-aa-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-aa-md ${trend === "up" ? "bg-green-100" : trend === "down" ? "bg-red-100" : "bg-aa-bg"}`}>
          <Icon className={`w-5 h-5 ${trend === "up" ? "text-green-600" : trend === "down" ? "text-red-600" : "text-gray-500"}`} />
        </div>
      </div>
    </div>
  );
}

// Expandable row for recent overrides
function RecentOverridesRow({ overrides }) {
  if (!overrides || overrides.length === 0) {
    return <p className="text-aa-xs text-gray-400 italic">No recent override examples</p>;
  }
  
  return (
    <div className="mt-2 space-y-1">
      <p className="text-aa-xs font-medium text-gray-500 mb-2">Recent Overrides:</p>
      {overrides.map((o, idx) => (
        <div key={idx} className="flex items-center gap-4 text-aa-xs text-gray-600 bg-gray-50 px-3 py-1.5 rounded">
          <span className="font-mono text-gray-400">{o.job_number}</span>
          <span className="truncate max-w-[150px]">{o.customer_name || "—"}</span>
          <span>{o.quantity} units</span>
          <span className="text-gray-400">{formatCurrency(o.original_price)} →</span>
          <span className="font-medium">{formatCurrency(o.final_price)}</span>
          <span className={o.price_delta > 0 ? "text-green-600" : o.price_delta < 0 ? "text-red-600" : "text-gray-400"}>
            {formatDelta(o.price_delta)}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function PricingIntelligence() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: "override_percentage", direction: "desc" });
  
  const getToken = () => localStorage.getItem("auth_token");
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/reports/pricing-intelligence`, { headers });
      setData(res.data);
    } catch (error) {
      console.error("Failed to load pricing intelligence:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
      } else {
        toast.error("Failed to load report");
      }
    } finally {
      setLoading(false);
    }
  };
  
  const toggleRow = (productName) => {
    setExpandedRows(prev => ({
      ...prev,
      [productName]: !prev[productName]
    }));
  };
  
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc"
    }));
  };
  
  const sortedProducts = useMemo(() => {
    if (!data?.products) return [];
    
    return [...data.products].sort((a, b) => {
      const aVal = a[sortConfig.key] ?? 0;
      const bVal = b[sortConfig.key] ?? 0;
      
      if (sortConfig.direction === "asc") {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
  }, [data?.products, sortConfig]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-aa-bg">
        <MainNav />
        <div className="max-w-aa-container mx-auto px-6 py-12">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-2 border-aa-primary border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-gray-500">Loading pricing intelligence...</p>
          </div>
        </div>
      </div>
    );
  }
  
  const summary = data?.summary || {};
  const products = sortedProducts;
  
  return (
    <div className="min-h-screen bg-aa-bg">
      <MainNav />
      
      {/* Page Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-aa-container mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-aa-md bg-aa-primary/10">
              <BarChart3 className="w-5 h-5 text-aa-primary" />
            </div>
            <div>
              <h1 className="text-aa-xl font-bold text-gray-900">Pricing Intelligence</h1>
              <p className="text-aa-xs text-gray-500">Analyze pricing patterns and override trends</p>
            </div>
          </div>
        </div>
      </div>
      
      <main className="max-w-aa-container mx-auto px-6 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <SummaryCard
            title="Products Overridden"
            value={formatPercent(summary.override_percentage)}
            subtitle={`${summary.products_with_overrides} of ${summary.total_products} products`}
            icon={Percent}
            description="Share of products where you manually changed the calculated price on at least one quote line."
          />
          <SummaryCard
            title="Avg Price Delta"
            value={formatDelta(summary.avg_price_delta)}
            subtitle="Per unit adjustment"
            icon={summary.avg_price_delta >= 0 ? TrendingUp : TrendingDown}
            trend={summary.avg_price_delta > 0 ? "up" : summary.avg_price_delta < 0 ? "down" : null}
            description="Average per-unit difference between your overridden price and the system-calculated price. Positive = you charged more, negative = you discounted."
          />
          <SummaryCard
            title="Override Revenue"
            value={formatCurrency(summary.total_revenue_from_overrides)}
            subtitle="Total from overridden lines"
            icon={DollarSign}
            description="Total revenue (ex GST) from quote lines where the price was manually overridden."
          />
          <SummaryCard
            title="Most Overridden"
            value={summary.most_overridden_product || "—"}
            subtitle="Product with highest override %"
            icon={Package}
            description="The product you adjust the price on most often — a candidate for reviewing its base pricing."
          />
        </div>
        
        {/* Empty State */}
        {products.length === 0 ? (
          <div className="bg-aa-card rounded-aa-lg shadow-aa-card p-12 text-center">
            <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No Pricing Data Yet</h3>
            <p className="text-aa-sm text-gray-500">
              Pricing override data will appear here once you start adjusting prices on quotes.
            </p>
          </div>
        ) : (
          /* Main Table */
          <div className="bg-aa-card rounded-aa-lg shadow-aa-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="pricing-intelligence-table">
                <thead className="bg-aa-bg border-b border-gray-200">
                  <tr>
                    <th className="w-8 px-4 py-3"></th>
                    <th 
                      className="px-4 py-3 text-left text-aa-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("product_name")}
                    >
                      <span className="flex items-center gap-1">
                        Product Name
                        <ArrowUpDown className="w-3 h-3" />
                      </span>
                    </th>
                    <th className="px-4 py-3 text-left text-aa-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Type
                    </th>
                    <th 
                      className="px-4 py-3 text-right text-aa-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("total_quotes")}
                      title="Number of quote lines that included this product in the selected period."
                    >
                      <span className="flex items-center justify-end gap-1">
                        Quotes
                        <ArrowUpDown className="w-3 h-3" />
                      </span>
                    </th>
                    <th 
                      className="px-4 py-3 text-right text-aa-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("total_overrides")}
                      title="How many of those quote lines had a manual price override."
                    >
                      <span className="flex items-center justify-end gap-1">
                        Overrides
                        <ArrowUpDown className="w-3 h-3" />
                      </span>
                    </th>
                    <th 
                      className="px-4 py-3 text-right text-aa-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("override_percentage")}
                      title="Overrides ÷ Quotes — how often this product's price is manually adjusted."
                    >
                      <span className="flex items-center justify-end gap-1">
                        Override %
                        <ArrowUpDown className="w-3 h-3" />
                      </span>
                    </th>
                    <th 
                      className="px-4 py-3 text-right text-aa-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("avg_effective_margin")}
                      title="Average profit margin actually achieved on this product across quotes (after any overrides)."
                    >
                      <span className="flex items-center justify-end gap-1">
                        Avg Margin
                        <ArrowUpDown className="w-3 h-3" />
                      </span>
                    </th>
                    <th 
                      className="px-4 py-3 text-right text-aa-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("avg_price_delta")}
                      title="Average per-unit difference between the overridden price and the calculated price for this product."
                    >
                      <span className="flex items-center justify-end gap-1">
                        Avg Δ Price
                        <ArrowUpDown className="w-3 h-3" />
                      </span>
                    </th>
                    <th 
                      className="px-4 py-3 text-right text-aa-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("total_revenue_from_overrides")}
                      title="Total revenue (ex GST) generated from lines where this product's price was overridden."
                    >
                      <span className="flex items-center justify-end gap-1">
                        Revenue
                        <ArrowUpDown className="w-3 h-3" />
                      </span>
                    </th>
                    <th className="px-4 py-3 text-left text-aa-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Insight
                    </th>
                    <th className="px-4 py-3 text-left text-aa-xs font-semibold text-gray-600 uppercase tracking-wider">
                      <span className="flex items-center gap-1">
                        <Lightbulb className="w-3 h-3" />
                        Suggested
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {products.map((product) => {
                    const isExpanded = expandedRows[product.product_name];
                    const isHighlighted = product.override_percentage > 20;
                    
                    return (
                      <React.Fragment key={product.product_name}>
                        <tr 
                          className={`hover:bg-gray-50 transition-colors ${isHighlighted ? "bg-amber-50/50" : ""}`}
                          data-testid={`product-row-${product.product_name}`}
                        >
                          <td className="px-4 py-4">
                            {product.recent_overrides?.length > 0 && (
                              <button
                                onClick={() => toggleRow(product.product_name)}
                                className="p-1 hover:bg-gray-100 rounded"
                                data-testid={`expand-${product.product_name}`}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-gray-400" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-gray-400" />
                                )}
                              </button>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-aa-sm font-medium text-gray-900">
                              {product.product_name}
                            </span>
                            {product.supplier && (
                              <span className="ml-2 text-aa-xs text-gray-400">{product.supplier}</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-aa-sm text-gray-600">{product.product_type || "—"}</span>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <span className="text-aa-sm text-gray-700">{product.total_quotes}</span>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <span className="text-aa-sm font-medium text-gray-900">{product.total_overrides}</span>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <span className={`text-aa-sm font-semibold ${
                              product.override_percentage > 40 ? "text-amber-600" :
                              product.override_percentage > 20 ? "text-amber-500" : "text-gray-700"
                            }`}>
                              {formatPercent(product.override_percentage)}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <span className="text-aa-sm text-gray-700">
                              {formatPercent(product.avg_effective_margin)}
                            </span>
                            {product.avg_global_margin !== product.avg_effective_margin && (
                              <span className="text-aa-xs text-gray-400 ml-1">
                                (vs {formatPercent(product.avg_global_margin)})
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-right">
                            <span className={`text-aa-sm font-medium ${
                              product.avg_price_delta > 0 ? "text-green-600" :
                              product.avg_price_delta < 0 ? "text-red-600" : "text-gray-500"
                            }`}>
                              {formatDelta(product.avg_price_delta)}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <span className="text-aa-sm text-gray-700">
                              {formatCurrency(product.total_revenue_from_overrides)}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <InsightBadge insight={product.insight} />
                          </td>
                          <td className="px-4 py-4">
                            {product.margin_recommendation ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-aa-sm bg-blue-50 text-blue-700 text-aa-xs font-medium">
                                <Lightbulb className="w-3 h-3" />
                                {product.margin_recommendation}
                              </span>
                            ) : (
                              <span className="text-aa-xs text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="bg-gray-50">
                            <td colSpan={11} className="px-8 py-4">
                              <div className="grid grid-cols-4 gap-4 mb-4 text-aa-xs">
                                <div>
                                  <span className="text-gray-500">Total Qty Quoted:</span>
                                  <span className="ml-2 font-medium">{product.total_quantity_quoted}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Avg Qty (Overridden):</span>
                                  <span className="ml-2 font-medium">{product.avg_quantity_when_overridden}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Avg Unit Sell:</span>
                                  <span className="ml-2 font-medium">{formatCurrency(product.avg_unit_sell_price)}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Avg Unit Cost:</span>
                                  <span className="ml-2 font-medium">{formatCurrency(product.avg_unit_true_cost)}</span>
                                </div>
                              </div>
                              <RecentOverridesRow overrides={product.recent_overrides} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
